import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ButtonPropsWithLabel } from 'src/components/Dialog';
import {
  fetchInvoices,
  isPayableOpenInvoice,
  pastDueInvoicesQueryKey,
} from 'src/models/payment/payment';
import { formatCurrency } from 'src/utils/numberFormatter';

import { BlockingReasonFallback, BlockingReasonRow } from './BlockingReasonRow';

export function PaymentRequiredReason({
  actions,
  userId,
  resolved = false,
}: {
  actions?: ButtonPropsWithLabel[];
  userId: string;
  resolved?: boolean;
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });

  const { data: invoicesPayload, isLoading: isLoadingInvoices } = useQuery({
    queryKey: pastDueInvoicesQueryKey(userId),
    queryFn: () =>
      fetchInvoices({
        userId,
        per: 100,
        filters: { pastDue: true },
      }),
  });

  const invoices = invoicesPayload?.invoices ?? [];

  const payableInvoices = invoices.filter(isPayableOpenInvoice);

  const totalAmountDue = payableInvoices.reduce(
    (acc, invoice) => acc + (invoice.amountDue ?? 0),
    0
  );

  if (isLoadingInvoices) {
    return <BlockingReasonFallback />;
  }

  return (
    <BlockingReasonRow
      title={
        totalAmountDue > 0
          ? t('blockingReasons.paymentRequired.title', {
              amount: formatCurrency(totalAmountDue),
            })
          : t('blockingReasons.paymentRequired.titleNoAmount')
      }
      message={t('blockingReasons.paymentRequired.message')}
      actions={resolved ? undefined : actions}
      color={resolved ? 'success' : 'error'}
    />
  );
}
